import {
  mapAssetLibrariesListResponseDto,
  mapAssetLibraryOverviewResponseDto,
  mapAssetLibrarySummaryDto,
  mapGroupLibraryItemsResponseDto,
  mapLibraryImportBatchResponseDto,
  mapLibraryImportedAssetDto,
  mapLibraryItemDto,
  mapLibraryItemsListResponseDto,
  type AssetDownloadUrl,
  type AssetDownloadUrlDto,
  type AssetLibrariesListResponseDto,
  type AssetLibraryOverviewEntry,
  type AssetLibraryOverviewResponseDto,
  type AssetLibrarySummary,
  type AssetLibrarySummaryDto,
  type GroupLibraryItemsResponse,
  type GroupLibraryItemsResponseDto,
  type LibraryImportBatchResponseDto,
  type LibraryImportBatchResult,
  type LibraryImportedAsset,
  type LibraryImportedAssetDto,
  type LibraryItem,
  type LibraryItemDto,
  type LibraryItemRole,
  type LibraryItemsListResponseDto,
  ApiHttpError,
  mapLibraryTextContentDto,
  serializeCopyAssets,
  type LibraryTextContentDto,
} from '@/types';

import type {
  AddLibraryItemRequest,
  AssetLibrariesService,
  SaveNodeToLibraryRequest,
  SaveProjectAssetToLibraryRequest,
  SaveUploadToLibraryOptions,
} from '../assetLibrariesService';

import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`AssetLibrariesService`(T7 / docs/53 Part A)。
 *
 * `/api/asset_libraries` 非 GROUP_HEADER_EXEMPT → `httpRequestWithRefresh` 自動帶
 * `X-Current-Group`(不需 extraHeaders);兩支皆 GET,免 CSRF。
 */

/** `listItems` 續讀頁數上限(BE 每頁預設 100 → 2000 筆)。防 cursor 迴圈,不是業務上限。 */
const ITEMS_MAX_PAGES = 20;

const PATH = {
  libraries: '/api/asset_libraries',
  items: (libId: string) => `/api/asset_libraries/${encodeURIComponent(libId)}/items`,
  itemsFromProjectAsset: (libId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/from_project_asset`,
  // R270 / ADR-044 — 文案藍卡 → 素材庫(文案卡沒有 ProjectAsset,見 service interface)。
  itemsFromNode: (libId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/from_node`,
  // docs/59 四支
  importFromLibraryBatch: (projectId: string) =>
    `/api/projects/${encodeURIComponent(projectId)}/assets/import_from_library_batch`,
  importFromLibrary: (projectId: string) =>
    `/api/projects/${encodeURIComponent(projectId)}/assets/import_from_library`,
  itemTextContent: (libId: string, itemId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/${encodeURIComponent(itemId)}/text-content`,
  itemDownloadUrl: (libId: string, itemId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/${encodeURIComponent(itemId)}/download-url`,
  itemsFromUpload: (libId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/from_upload`,
  groupItems: '/api/asset_libraries/items',
  // 單一 item 的 DELETE(軟刪)/ PATCH(metadata 局部更新)— BE 2026-08-07 交付
  item: (libId: string, itemId: string) =>
    `/api/asset_libraries/${encodeURIComponent(libId)}/items/${encodeURIComponent(itemId)}`,
  overview: '/api/asset_libraries/overview',
} as const;

export function createHttpAssetLibrariesService(
  config: HttpClientConfig,
): AssetLibrariesService {
  return {
    async listLibraries(): Promise<AssetLibrarySummary[]> {
      const env = await httpRequestWithRefresh<AssetLibrariesListResponseDto>(
        config,
        'GET',
        PATH.libraries,
      );
      return mapAssetLibrariesListResponseDto(
        unwrapResult(env, 'assetLibraries.listLibraries'),
      );
    },

    /**
     * R188 — 翻完所有頁再回。BE 2026-08-02 目錄化前是**硬截 100 筆、無 cursor**
     * (庫內第 101 筆起靜默消失 —— 主體庫一多,使用者看到「產品不見了」而不是錯誤)。
     *
     * 相容兩邊:第一頁不帶任何 query param(= 舊 build 的既有行為),只有拿到
     * `next_cursor` 才續讀;舊 build 不回這欄 → 一頁收工。
     *
     * ⚠️ **cursor 綁 filter**(BE 2026-08-02):之後若加 `?role=` / `?tag=` server-side
     * filter,換 filter 一定要**從第一頁重拉**,沿用手上的 cursor 會 422 `INVALID_CURSOR`
     * (BE 刻意 fail-loud,取代原本會靜默漏件的續讀)。另:filter 需要 Firestore composite
     * index,**index 未 deploy 時是 FAILED_PRECONDITION 500 而不是空結果** —— 切 filter
     * 前先確認該環境 index 已建。
     */
    async listItems(
      libId: string,
      opts?: { tag?: string; q?: string; role?: LibraryItemRole },
    ): Promise<LibraryItem[]> {
      // R240 —— `q` 是**搜尋**不是瀏覽:BE 忽略 cursor、無分頁、上限 100
      // (`service.py:1090` `SEARCH_ITEMS_MAX`)。所以走單發,不進翻頁迴圈
      // (進了會「第一頁就 next_cursor=null」看似正常,實際靜默截在 100)。
      if (opts?.q) {
        const env = await httpRequestWithRefresh<LibraryItemsListResponseDto>(
          config,
          'GET',
          `${PATH.items(libId)}?q=${encodeURIComponent(opts.q)}`,
        );
        return mapLibraryItemsListResponseDto(
          unwrapResult(env, 'assetLibraries.listItems'),
        );
      }
      const tagParam = opts?.tag ? `tag=${encodeURIComponent(opts.tag)}` : '';
      // docs/107 A-4 — `?role=` server-side filter(目錄化參考素材;不帶 = 預設清單)
      const roleParam = opts?.role ? `role=${encodeURIComponent(opts.role)}` : '';
      const out: LibraryItem[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < ITEMS_MAX_PAGES; page += 1) {
        // 型別註記不可省:`cursor` 由 `res.next_cursor` 回寫,不標會讓 tsc 判成
        // 自我參照的循環推導(TS7022)。
        // ⚠️ cursor 綁 filter —— `tag` / `role` 一旦帶了就每頁都要帶同一個值,
        // 換 filter 必須從第一頁重拉(BE 422 `INVALID_CURSOR`,刻意 fail-loud)
        const qs = [roleParam, tagParam, cursor ? `cursor=${encodeURIComponent(cursor)}` : '']
          .filter(Boolean)
          .join('&');
        const url: string = qs ? `${PATH.items(libId)}?${qs}` : PATH.items(libId);
        const env = await httpRequestWithRefresh<LibraryItemsListResponseDto>(config, 'GET', url);
        const res: LibraryItemsListResponseDto = unwrapResult(env, 'assetLibraries.listItems');
        out.push(...mapLibraryItemsListResponseDto(res));
        cursor = typeof res.next_cursor === 'string' && res.next_cursor ? res.next_cursor : null;
        if (!cursor) return out;
      }
      // 走到這裡 = 超過 2000 筆。截斷要出聲,不要假裝撈完了(無聲截斷紅線)。
      console.warn('[assetLibraries] listItems hit page cap; remaining items not loaded', libId, out.length);
      return out;
    },

    async listItemsPage(
      libId: string,
      opts?: { tag?: string; limit?: number; cursor?: string | null },
    ): Promise<{ items: LibraryItem[]; nextCursor: string | null }> {
      const params = new URLSearchParams();
      if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
      if (opts?.tag) params.set('tag', opts.tag);
      if (opts?.cursor) params.set('cursor', opts.cursor);
      const qs = params.toString();
      const env = await httpRequestWithRefresh<LibraryItemsListResponseDto>(
        config,
        'GET',
        qs ? `${PATH.items(libId)}?${qs}` : PATH.items(libId),
      );
      const res = unwrapResult(env, 'assetLibraries.listItemsPage');
      return {
        items: mapLibraryItemsListResponseDto(res),
        // 翻完 = next_cursor 為 null(不是「筆數 < limit」—— BE 明文)
        nextCursor: typeof res.next_cursor === 'string' && res.next_cursor ? res.next_cursor : null,
      };
    },

    async createLibrary(name: string): Promise<AssetLibrarySummary> {
      const env = await httpRequestWithRefresh<AssetLibrarySummaryDto>(
        config,
        'POST',
        PATH.libraries,
        { name },
      );
      return mapAssetLibrarySummaryDto(unwrapResult(env, 'assetLibraries.createLibrary'));
    },

    async saveProjectAssetToLibrary(
      libId: string,
      req: SaveProjectAssetToLibraryRequest,
    ): Promise<LibraryItem> {
      // R292.5(docs/107 A-4)— `role` / `metadata` 是 BE **既有欄位**(2026-08-02
      // 目錄化;先前這裡的「只帶 project_id/asset_id」註解過時)。空值不送
      // (extra=forbid 但欄位選填,body 形狀不漂)。
      const body: Record<string, unknown> = { project_id: req.projectId, asset_id: req.assetId };
      if (req.role) body.role = req.role;
      if (req.tags && req.tags.length > 0) body.tags = req.tags;
      if (req.metadata && Object.keys(req.metadata).length > 0) body.metadata = req.metadata;
      const env = await httpRequestWithRefresh<LibraryItemDto>(
        config,
        'POST',
        PATH.itemsFromProjectAsset(libId),
        body,
      );
      return mapLibraryItemDto(unwrapResult(env, 'assetLibraries.saveProjectAssetToLibrary'));
    },

    async saveNodeToLibrary(
      libId: string,
      req: SaveNodeToLibraryRequest,
    ): Promise<LibraryItem> {
      // body 只帶 project_id/node_id(+選填 metadata);內容與 envelope 一律後端組
      // (`extra="forbid"` — 夾帶 copy_assets 之類的欄位會 422)。
      const body: Record<string, unknown> = { project_id: req.projectId, node_id: req.nodeId };
      if (req.tags && req.tags.length > 0) body.tags = req.tags;
      if (req.metadata !== undefined) body.metadata = req.metadata;
      const env = await httpRequestWithRefresh<LibraryItemDto>(
        config,
        'POST',
        PATH.itemsFromNode(libId),
        body,
      );
      return mapLibraryItemDto(unwrapResult(env, 'assetLibraries.saveNodeToLibrary'));
    },

    // === docs/59 四支 ===

    async importFromLibrary(
      projectId: string,
      req: { libId: string; itemId: string },
    ): Promise<LibraryImportedAsset> {
      const env = await httpRequestWithRefresh<LibraryImportedAssetDto>(
        config,
        'POST',
        PATH.importFromLibrary(projectId),
        { lib_id: req.libId, item_id: req.itemId },
      );
      return mapLibraryImportedAssetDto(unwrapResult(env, 'assetLibraries.importFromLibrary'));
    },

    async getItemDownloadUrl(libId: string, itemId: string): Promise<AssetDownloadUrl> {
      const env = await httpRequestWithRefresh<AssetDownloadUrlDto>(
        config,
        'GET',
        PATH.itemDownloadUrl(libId, itemId),
      );
      const dto = unwrapResult(env, 'assetLibraries.getItemDownloadUrl');
      return {
        downloadUrl: dto.download_url,
        downloadUrlExpiresDatetime: dto.download_url_expires_datetime,
        mimeType: dto.mime_type,
      };
    },

    async saveUploadToLibrary(
      libId: string,
      uploadId: string,
      opts?: SaveUploadToLibraryOptions,
    ): Promise<LibraryItem> {
      // body 送 upload_id(kind 後端從 upload 判定;extra=forbid)+ 選填 metadata(R184
      // Gap A)+ typed `group_key` / `tags` / `role`(docs/57 §4b + docs/107 A-4)。
      // **空值不送**:BE 是 `extra="forbid"` 但欄位本身選填,送 undefined/空陣列只會讓
      // body 形狀漂。R292.5 起主體上傳帶 `role`(見 service 介面註解 —— 「不送 role」
      // 是過時取捨,owner 已拍板主體圖不進素材庫頁清單、picker 走 `?role=`)。
      const body: Record<string, unknown> = { upload_id: uploadId };
      if (opts?.metadata && Object.keys(opts.metadata).length > 0) body.metadata = opts.metadata;
      if (opts?.groupKey) body.group_key = opts.groupKey;
      if (opts?.tags && opts.tags.length > 0) body.tags = opts.tags;
      if (opts?.role) body.role = opts.role;
      const env = await httpRequestWithRefresh<LibraryItemDto>(
        config,
        'POST',
        PATH.itemsFromUpload(libId),
        body,
      );
      return mapLibraryItemDto(unwrapResult(env, 'assetLibraries.saveUploadToLibrary'));
    },

    async addItem(libId: string, req: AddLibraryItemRequest): Promise<LibraryItem> {
      // BE `extra="forbid"` → 選填欄空值一律不送(送 undefined 會讓 body 形狀漂)。
      const body: Record<string, unknown> = {
        asset_kind: req.assetKind,
        source: req.source,
        gcs_uri: req.gcsUri,
      };
      if (req.sourceAdAccountCid) body.source_ad_account_cid = req.sourceAdAccountCid;
      if (req.metadata && Object.keys(req.metadata).length > 0) body.metadata = req.metadata;
      if (req.hashMethod && Object.keys(req.hashMethod).length > 0) body.hash_method = req.hashMethod;
      if (req.tags && req.tags.length > 0) body.tags = req.tags;
      if (req.groupKey) body.group_key = req.groupKey;
      const env = await httpRequestWithRefresh<LibraryItemDto>(
        config,
        'POST',
        PATH.items(libId),
        body,
      );
      return mapLibraryItemDto(unwrapResult(env, 'assetLibraries.addItem'));
    },

    async importFromLibraryBatch(
      projectId: string,
      items: Array<{ libId: string; itemId: string; metadata?: Record<string, unknown> }>,
    ): Promise<LibraryImportBatchResult> {
      const env = await httpRequestWithRefresh<LibraryImportBatchResponseDto>(
        config,
        'POST',
        PATH.importFromLibraryBatch(projectId),
        {
          // R375(pa.4)— metadata 只在有內容時帶(缺席 = 舊行為;BE 選填)。
          items: items.map((i) => ({
            lib_id: i.libId,
            item_id: i.itemId,
            ...(i.metadata && Object.keys(i.metadata).length > 0 ? { metadata: i.metadata } : {}),
          })),
        },
      );
      return mapLibraryImportBatchResponseDto(
        unwrapResult(env, 'assetLibraries.importFromLibraryBatch'),
      );
    },

    /**
     * group 全庫 items 聚合(docs/59 #4)。
     *
     * R212 — BE 補了四欄(docs/88 §9 / docs/87 §4):`check_badge` / `checked_at` /
     * `ga_uploaded_at` / `source_project_name`,由 `mapLibraryItemDto` 一併映射
     * (**清單「檢查結果」欄的伺服端真相**,批次 store 只覆蓋剛跑完那批)。
     * 這裡不需要改動:四欄都是 item 層的 optional 欄,舊 build 不回就是 null。
     *
     * **翻完所有頁再回**(同 `listItems` 的理由與寫法)。BE 2026-08-02 目錄化後這支
     * 每次最多回 500 筆並帶 `next_cursor`,而在此之前 FE 是單發不續讀 —— 工作區素材
     * 超過 500 筆時第 501 筆起在 /assets **整個消失**(選不到、檢查不到,連 warn 都
     * 沒有)。且 `/assets` 的篩選、排序、分頁、CSV 匯出、「選取符合條件的全部 N 項」
     * 全是 client-side,吃的都是這裡回的集合 —— 少一筆就是全部一起少。
     */
    /**
     * R240 —— 跨庫檔名搜尋。**含隱藏的廣告素材庫**(它在搜尋面的唯一入口);
     * 單發不翻頁(BE `q` 出現時忽略 cursor、`next_cursor` 恆 null、上限 100)。
     */
    async searchGroupItems(q: string): Promise<GroupLibraryItemsResponse> {
      const env = await httpRequestWithRefresh<GroupLibraryItemsResponseDto>(
        config,
        'GET',
        `${PATH.groupItems}?q=${encodeURIComponent(q)}`,
      );
      return mapGroupLibraryItemsResponseDto(
        unwrapResult(env, 'assetLibraries.searchGroupItems'),
      );
    },

    async listLibraryOverview(): Promise<AssetLibraryOverviewEntry[]> {
      const env = await httpRequestWithRefresh<AssetLibraryOverviewResponseDto>(
        config,
        'GET',
        PATH.overview,
      );
      return mapAssetLibraryOverviewResponseDto(
        unwrapResult(env, 'assetLibraries.listLibraryOverview'),
      );
    },

    async listItemsBySourceAsset(assetId: string): Promise<GroupLibraryItemsResponse['items']> {
      // 同源反查(docs/107 追補;短路語意同 `?q=`:無分頁、next_cursor 恆 null)
      const env = await httpRequestWithRefresh<GroupLibraryItemsResponseDto>(
        config,
        'GET',
        `${PATH.groupItems}?source_project_asset_id=${encodeURIComponent(assetId)}`,
      );
      return mapGroupLibraryItemsResponseDto(
        unwrapResult(env, 'assetLibraries.listItemsBySourceAsset'),
      ).items;
    },

    async listGroupItems(): Promise<GroupLibraryItemsResponse> {
      const items: GroupLibraryItemsResponse['items'] = [];
      let libraryCount = 0;
      let cursor: string | null = null;
      for (let page = 0; page < ITEMS_MAX_PAGES; page += 1) {
        // 型別註記不可省(同 listItems:cursor 由 res.next_cursor 回寫,不標會 TS7022)
        const url: string = cursor
          ? `${PATH.groupItems}?cursor=${encodeURIComponent(cursor)}`
          : PATH.groupItems;
        const env = await httpRequestWithRefresh<GroupLibraryItemsResponseDto>(config, 'GET', url);
        const res: GroupLibraryItemsResponseDto = unwrapResult(
          env,
          'assetLibraries.listGroupItems',
        );
        const mapped = mapGroupLibraryItemsResponseDto(res);
        items.push(...mapped.items);
        // 庫數是整體統計不是分頁量,取最大值(理論上每頁一致)
        libraryCount = Math.max(libraryCount, mapped.libraryCount);
        cursor = typeof res.next_cursor === 'string' && res.next_cursor ? res.next_cursor : null;
        if (!cursor) return { items, libraryCount };
      }
      // 走到這裡 = 超過 10,000 筆。截斷要出聲,不要假裝撈完了(無聲截斷紅線)。
      console.warn(
        '[assetLibraries] listGroupItems hit page cap; remaining items not loaded',
        items.length,
      );
      return { items, libraryCount };
    },

    async listGroupItemsPage(opts) {
      // R376 — 單頁聚合(picker「固定小批量 + 載入更多」);query 缺席 = BE 預設。
      const params = new URLSearchParams();
      if (opts?.role !== undefined) params.set('role', opts.role);
      if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
      if (opts?.cursor !== undefined) params.set('cursor', opts.cursor);
      const qs = params.toString();
      const env = await httpRequestWithRefresh<GroupLibraryItemsResponseDto>(
        config,
        'GET',
        qs ? `${PATH.groupItems}?${qs}` : PATH.groupItems,
      );
      const res = unwrapResult(env, 'assetLibraries.listGroupItemsPage');
      const mapped = mapGroupLibraryItemsResponseDto(res);
      return {
        ...mapped,
        nextCursor: typeof res.next_cursor === 'string' && res.next_cursor ? res.next_cursor : null,
      };
    },

    // ── item 管理三支(BE 2026-08-07 交付 DELETE + PATCH;`ASSET_ITEM_MUTATION_SUPPORTED`
    //    控 UI 可用性)──

    async getItemTextContent(libId, itemId) {
      const env = await httpRequestWithRefresh<LibraryTextContentDto>(
        config,
        'GET',
        PATH.itemTextContent(libId, itemId),
      );
      const content = mapLibraryTextContentDto(
        unwrapResult(env, 'assetLibraries.getItemTextContent'),
      );
      // 未知 kind(BE 之後的 v2 envelope)→ 誠實 throw 給呼叫端顯「讀取失敗」,
      // 不硬塞 plain_text 分支(R270.2 紅線:讀不到 ≠ 不存在,也 ≠ 亂讀)。
      if (content === null) {
        throw new ApiHttpError(200, {
          code: 'TEXT_CONTENT_UNSUPPORTED_KIND',
          message: 'unrecognized text-content kind',
        });
      }
      return content;
    },

    async putItemTextContent(libId, itemId, content) {
      // R292.9 — 判別 union 對齊 GET 兩分支(`kind` 由現檔決定,呼叫端不得自己換)。
      const body: Record<string, unknown> =
        content.kind === 'copyAssets'
          ? { kind: 'copy_assets_v1', assets: serializeCopyAssets(content.assets) }
          : { kind: 'plain_text', plain_text: content.text };
      const env = await httpRequestWithRefresh<LibraryTextContentDto>(
        config,
        'PUT',
        PATH.itemTextContent(libId, itemId),
        body,
      );
      // BE 直接回更新後內容(同 GET shape)→ 不必二次 GET。未知 kind 的處理同 GET:
      // 誠實 throw,不硬塞分支。
      const saved = mapLibraryTextContentDto(
        unwrapResult(env, 'assetLibraries.putItemTextContent'),
      );
      if (saved === null) {
        throw new ApiHttpError(200, {
          code: 'TEXT_CONTENT_UNSUPPORTED_KIND',
          message: 'unrecognized text-content kind',
        });
      }
      return saved;
    },

    /** 軟刪除(doc 留存供 audit,清單一律隱形)。**BE 無 restore 端點** → UI 不做 undo。 */
    async deleteItem(libId: string, itemId: string): Promise<void> {
      await httpRequestWithRefresh(config, 'DELETE', PATH.item(libId, itemId));
    },

    async renameItem(libId: string, itemId: string, name: string): Promise<LibraryItem> {
      // metadata **欄位級**局部更新(BE 以 `metadata.<key>` 逐鍵 update,不整包 set)
      // → 只送 name 不會洗掉 R184 存在 metadata 的 product_id / view / 尺寸分組鍵。
      const env = await httpRequestWithRefresh<LibraryItemDto>(
        config,
        'PATCH',
        PATH.item(libId, itemId),
        { metadata: { name } },
      );
      return mapLibraryItemDto(unwrapResult(env, 'assetLibraries.renameItem'));
    },

    async updateItemMetadata(
      libId: string,
      itemId: string,
      metadata: Record<string, unknown>,
    ): Promise<LibraryItem> {
      // 同 renameItem 端點,一次補多鍵(R292:匯入後補顯示名 + 主體分組欄)。
      // 欄位級 update → 只動送出的鍵,既有 metadata 不被洗掉。
      const env = await httpRequestWithRefresh<LibraryItemDto>(
        config,
        'PATCH',
        PATH.item(libId, itemId),
        { metadata },
      );
      return mapLibraryItemDto(unwrapResult(env, 'assetLibraries.updateItemMetadata'));
    },

    async moveItemToProject(libId: string, itemId: string, projectId: string): Promise<LibraryItem> {
      // ⚠️ **BE 資料模型未定,現在打必 422**:`LibraryItemPatchRequest` 是
      // `extra="forbid"` 且只收 `metadata`(item 沒有專案歸屬欄 — FE 待答 D 節)。
      // UI 的「移至專案」因此維持 disabled;BE 定案後改這裡的 body 即可。
      const env = await httpRequestWithRefresh<LibraryItemDto>(
        config,
        'PATCH',
        PATH.item(libId, itemId),
        { project_id: projectId },
      );
      return mapLibraryItemDto(unwrapResult(env, 'assetLibraries.moveItemToProject'));
    },
  };
}
